import { DOCUMENT_DEFINITIONS, DOCUMENT_TYPE_ORDER } from '@/features/case-workspace/schemas'
import {
  CBP_7501_LINE_ITEM_FIELDS,
} from '@/features/case-workspace/cbp7501'
import {
  COMMERCIAL_INVOICE_LINE_ITEM_FIELDS,
} from '@/features/case-workspace/invoiceTemplate'
import {
  MANIFEST_1302_SHIPMENT_FIELDS,
} from '@/features/case-workspace/manifest1302'
import {
  classifySourceDocument,
  labelForDocumentType,
  resolveSourceAutoPopulationStatus,
} from '@/features/case-workspace/mappings'
import type {
  AwaFieldRecommendation,
  CaseReviewState,
  DocumentSectionState,
  DocumentAuditEntry,
  DocumentFieldState,
  FieldValidationIssue,
  StructuredLineItemRecord,
  StructuredDocumentRecord,
  SupportedDocumentType,
  SourceDocumentRecord,
} from '@/features/case-workspace/types'
import type { Case, EntryField, EntryLine, IntakeDocument } from '@/lib/cases'

function nowIso() {
  return new Date().toISOString()
}

function formatBytes(sizeBytes: number) {
  return `${(sizeBytes / 1024).toFixed(1)} KB`
}

function seedField(
  value: string | number | null | undefined,
  origin: DocumentFieldState['origin'],
  actor: string,
): DocumentFieldState {
  const text = value == null ? '' : String(value)
  return {
    value: text,
    displayValue: text,
    origin,
    reviewStatus: origin === 'ai_extracted' ? 'extracted' : origin === 'manual' ? 'manually_entered' : undefined,
    sourceValue: origin === 'ai_extracted' || origin === 'uploaded_file' ? text : undefined,
    reviewedValue: origin === 'reviewed' ? text : undefined,
    lastUpdatedAt: nowIso(),
    lastUpdatedBy: actor,
  }
}

function buildSectionStates(actor: string, documentType: SupportedDocumentType) {
  return Object.fromEntries(
    DOCUMENT_DEFINITIONS[documentType].sections.map((section) => [
      section.id,
      {
        sectionId: section.id,
        status: 'not_started',
        lastUpdatedAt: nowIso(),
        lastUpdatedBy: actor,
      } satisfies DocumentSectionState,
    ]),
  )
}

function build7501LineItem(lines: EntryLine[], actor: string, index: number): StructuredLineItemRecord {
  const sourceLine = lines[index]
  const fields = Object.fromEntries(
    CBP_7501_LINE_ITEM_FIELDS.map((fieldDefinition) => {
      let value = ''
      let origin: DocumentFieldState['origin'] = 'manual'

      switch (fieldDefinition.key) {
        case 'line_number':
          value = String(index + 1).padStart(3, '0')
          origin = 'system'
          break
        case 'description_of_merchandise':
          value = sourceLine?.description ?? ''
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'htsus_number':
          value = sourceLine?.hts_code ?? ''
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'gross_weight':
        case 'manifest_quantity':
        case 'net_quantity_htsus_units':
          value = sourceLine?.quantity == null ? '' : String(sourceLine.quantity)
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'entered_value':
          value = sourceLine?.line_total == null ? '' : String(sourceLine.line_total)
          origin = value ? 'system' : 'manual'
          break
        case 'htsus_rate':
          value = sourceLine?.ad_valorem_rate == null ? '' : String(sourceLine.ad_valorem_rate)
          origin = value ? 'system' : 'manual'
          break
        case 'duty_and_ir_tax':
          value = sourceLine?.duty_amount == null ? '' : String(sourceLine.duty_amount)
          origin = value ? 'system' : 'manual'
          break
      }

      return [fieldDefinition.key, seedField(value, origin, actor)]
    }),
  )

  return {
    id: crypto.randomUUID(),
    lineNumber: String(index + 1).padStart(3, '0'),
    status: sourceLine ? 'in_progress' : 'not_started',
    fields,
    auditHistory: defaultAudit(actor, `CBP Form 7501 line ${String(index + 1).padStart(3, '0')} scaffolded`),
  }
}

function buildManifestShipmentLine(lines: EntryLine[], fields: EntryField[], actor: string, index: number): StructuredLineItemRecord {
  const sourceLine = lines[index]
  const entryField = (key: string) => pickField(fields, key)
  const lineFields = Object.fromEntries(
    MANIFEST_1302_SHIPMENT_FIELDS.map((fieldDefinition) => {
      let value = ''
      let origin: DocumentFieldState['origin'] = 'manual'

      switch (fieldDefinition.key) {
        case 'bill_of_lading_number':
          value = entryField('bill_of_lading')
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'goods_description':
          value = sourceLine?.description ?? ''
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'gross_weight':
          value = sourceLine?.quantity == null ? '' : String(sourceLine.quantity)
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'number_of_packages':
          value = sourceLine?.quantity == null ? '' : String(sourceLine.quantity)
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'country_of_origin':
          value = sourceLine?.country_of_origin ?? ''
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'consignee_name':
          value = entryField('consignee')
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'shipper_name':
          value = entryField('manufacturer_id') || entryField('shipper')
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'first_port_place_possession':
          value = entryField('country_of_origin')
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'weight_unit':
          value = 'kg'
          origin = 'system'
          break
        case 'shipment_reconciliation_status':
          value = 'needs_manual_review'
          origin = 'system'
          break
      }

      return [fieldDefinition.key, seedField(value, origin, actor)]
    }),
  )

  return {
    id: crypto.randomUUID(),
    lineNumber: String(index + 1).padStart(3, '0'),
    status: sourceLine ? 'in_progress' : 'not_started',
    fields: lineFields,
    auditHistory: defaultAudit(actor, `Manifest shipment row ${String(index + 1).padStart(3, '0')} scaffolded`),
  }
}

function buildCommercialInvoiceLineItem(lines: EntryLine[], fields: EntryField[], actor: string, index: number): StructuredLineItemRecord {
  const sourceLine = lines[index]
  const entryField = (key: string) => pickField(fields, key)
  const lineFields = Object.fromEntries(
    COMMERCIAL_INVOICE_LINE_ITEM_FIELDS.map((fieldDefinition) => {
      let value = ''
      let origin: DocumentFieldState['origin'] = 'manual'

      switch (fieldDefinition.key) {
        case 'line_number':
          value = String(index + 1).padStart(3, '0')
          origin = 'system'
          break
        case 'part_number':
          value = ''
          origin = 'manual'
          break
        case 'detailed_merchandise_description':
          value = sourceLine?.description ?? ''
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'hts_tariff_number':
          value = sourceLine?.hts_code ?? ''
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'country_of_origin_line':
          value = sourceLine?.country_of_origin ?? entryField('country_of_origin')
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'quantity':
        case 'packaging_count':
          value = sourceLine?.quantity == null ? '' : String(sourceLine.quantity)
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'quantity_uom':
          value = 'EA'
          origin = 'system'
          break
        case 'gross_weight':
        case 'net_weight':
          value = sourceLine?.quantity == null ? '' : String(sourceLine.quantity)
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'unit_price':
          value = sourceLine?.unit_value == null ? '' : String(sourceLine.unit_value)
          origin = value ? 'system' : 'manual'
          break
        case 'extended_line_value':
          value = sourceLine?.line_total == null ? '' : String(sourceLine.line_total)
          origin = value ? 'system' : 'manual'
          break
        case 'currency_line':
          value = 'USD'
          origin = 'system'
          break
        case 'manufacturer_supplier_reference':
          value = entryField('manufacturer_id')
          origin = value ? 'ai_extracted' : 'manual'
          break
        case 'packaging_type':
          value = 'Carton'
          origin = 'manual'
          break
        case 'line_discrepancy_status':
          value = 'needs_manual_review'
          origin = 'system'
          break
      }

      return [fieldDefinition.key, seedField(value, origin, actor)]
    }),
  )

  return {
    id: crypto.randomUUID(),
    lineNumber: String(index + 1).padStart(3, '0'),
    status: sourceLine ? 'in_progress' : 'not_started',
    fields: lineFields,
    auditHistory: defaultAudit(actor, `Commercial invoice line ${String(index + 1).padStart(3, '0')} scaffolded`),
  }
}

function defaultAudit(actor: string, detail: string): DocumentAuditEntry[] {
  return [
    {
      id: `${actor}-${detail}`,
      at: nowIso(),
      actor,
      action: 'seeded',
      detail,
    },
  ]
}

function pickField(fields: EntryField[], key: string) {
  return fields.find((field) => field.field_key === key)?.field_value ?? ''
}

function buildStructuredDocument(
  kase: Case,
  actor: string,
  documentType: SupportedDocumentType,
  fields: EntryField[],
  lines: EntryLine[],
  sourceDocs: SourceDocumentRecord[],
): StructuredDocumentRecord {
  const definition = DOCUMENT_DEFINITIONS[documentType]
  const sharedSourceIds = sourceDocs.map((doc) => doc.id)
  const valueMap: Record<string, DocumentFieldState> = {}

  for (const section of definition.sections) {
    for (const field of section.fields) {
      let value = ''
      let origin: DocumentFieldState['origin'] = 'manual'

      switch (documentType) {
        case 'cbp_7501':
          if (field.key === 'importer_of_record') {
            value = kase.importer_of_record ?? ''
            origin = 'system'
          } else if (field.key === 'port_of_entry') {
            value = kase.port_of_entry ?? ''
            origin = 'system'
          } else if (field.key === 'entry_number') {
            value = kase.case_number.replace(/[^A-Z0-9]/gi, '').slice(0, 11).padEnd(11, '0')
            origin = 'system'
          } else if (field.key === 'summary_date' || field.key === 'entry_date' || field.key === 'import_date') {
            value = new Date(kase.updated_at).toLocaleDateString('en-US')
            origin = 'system'
          } else if (field.key === 'total_entered_value') {
            value = kase.total_value_usd == null ? '' : String(kase.total_value_usd)
            origin = 'system'
          } else if (field.key === 'duty_total') {
            value = kase.total_duty_usd == null ? '' : String(kase.total_duty_usd)
            origin = 'system'
          } else if (field.key === 'declarant_name') {
            value = actor
            origin = 'system'
          } else {
            value = pickField(fields, field.key)
            origin = value ? 'ai_extracted' : 'manual'
          }
          break
        case 'manifest':
          if (field.key === 'manifest_intake_source') value = sourceDocs[0] ? 'uploaded_external_source' : 'unknown_needs_review'
          if (field.key === 'source_channel') value = sourceDocs[0]?.title ?? ''
          if (field.key === 'submission_method') value = sourceDocs[0] ? 'hybrid' : 'unknown'
          if (field.key === 'document_provenance') value = sourceDocs[0] ? `Structured from ${sourceDocs[0].title}` : ''
          if (field.key === 'transmission_status') value = 'pending_review'
          if (field.key === 'received_timestamp' || field.key === 'port_intake_timestamp' || field.key === 'last_edited_timestamp') value = new Date(kase.updated_at).toISOString().slice(0, 16).replace('T', ' ')
          if (field.key === 'uploaded_file_reference') value = sourceDocs[0]?.title ?? ''
          if (field.key === 'manifest_number') value = pickField(fields, 'manifest_number')
          if (field.key === 'carrier_name') value = pickField(fields, 'carrier_name')
          if (field.key === 'vessel_name') value = pickField(fields, 'vessel_name')
          if (field.key === 'arrival_date') value = pickField(fields, 'estimated_arrival')
          if (field.key === 'mode_of_transport') value = pickField(fields, 'mode_of_transport') || '11'
          if (field.key === 'last_foreign_port_before_us' || field.key === 'foreign_port_of_lading') value = pickField(fields, 'country_of_origin')
          if (field.key === 'us_port_of_discharge' || field.key === 'us_port_of_arrival') value = '4911'
          if (field.key === 'cross_document_discrepancy_status') value = 'needs_manual_review'
          if (field.key === 'risk_severity') value = 'moderate'
          if (field.key === 'validation_status') value = 'Needs review'
          if (field.key === 'extraction_source') value = sourceDocs[0] ? 'Uploaded document extraction' : 'Manual'
          if (field.key === 'ocr_confidence') value = sourceDocs[0]?.extractionConfidence == null ? '' : String(sourceDocs[0].extractionConfidence)
          if (field.key === 'system_confidence') value = sourceDocs[0]?.extractionConfidence == null ? '' : String(sourceDocs[0].extractionConfidence)
          if (field.key === 'agent_confirmed_status') value = 'N'
          if (field.key === 'last_edited_by') value = actor
          origin = value ? 'ai_extracted' : 'manual'
          if (['manifest_intake_source', 'submission_method', 'transmission_status', 'cross_document_discrepancy_status', 'risk_severity', 'validation_status', 'agent_confirmed_status'].includes(field.key)) {
            origin = 'system'
          }
          break
        case 'commercial_invoice':
          if (field.key === 'invoice_number') {
            value = `INV-${kase.case_number.replace(/[^A-Z0-9]/gi, '').slice(0, 8)}`
            origin = 'system'
          } else if (field.key === 'invoice_date') {
            value = new Date(kase.updated_at).toISOString().slice(0, 10)
            origin = 'system'
          } else if (field.key === 'invoice_type') {
            value = 'commercial_invoice'
            origin = 'system'
          } else if (field.key === 'document_source') {
            value = sourceDocs[0] ? 'uploaded_scan' : 'unknown_needs_review'
            origin = 'system'
          } else if (field.key === 'source_file_name') {
            value =
              sourceDocs.find((doc) => doc.mirrorsDocumentType === 'commercial_invoice')?.title
              ?? sourceDocs[0]?.title
              ?? ''
            origin = value ? 'system' : 'manual'
          } else if (field.key === 'port_of_entry_destination') {
            value = kase.port_of_entry ?? ''
            origin = 'system'
          } else if (field.key === 'customs_transaction_type') {
            value = 'sale_purchase'
            origin = 'system'
          } else if (field.key === 'customs_value_basis') {
            value = 'purchase_price'
            origin = 'system'
          } else if (field.key === 'currency_code') {
            value = 'USD'
            origin = 'system'
          } else if (field.key === 'buyer_name') {
            value = kase.importer_of_record ?? ''
            origin = 'system'
          } else if (field.key === 'seller_name') {
            value = pickField(fields, 'consignee')
            origin = value ? 'ai_extracted' : 'manual'
          } else if (field.key === 'consignee_name') {
            value = pickField(fields, 'consignee')
            origin = value ? 'ai_extracted' : 'manual'
          } else if (field.key === 'importer_of_record_name') {
            value = kase.importer_of_record ?? ''
            origin = 'system'
          } else if (field.key === 'country_of_origin') {
            value = pickField(fields, 'country_of_origin')
            origin = value ? 'ai_extracted' : 'manual'
          } else if (field.key === 'foreign_port_of_lading') {
            value = pickField(fields, 'country_of_origin')
            origin = value ? 'ai_extracted' : 'manual'
          } else if (field.key === 'port_of_discharge') {
            value = kase.port_of_entry ?? ''
            origin = 'system'
          } else if (field.key === 'carrier_name') {
            value = pickField(fields, 'carrier_name')
            origin = value ? 'ai_extracted' : 'manual'
          } else if (field.key === 'bill_of_lading_number') {
            value = pickField(fields, 'bill_of_lading')
            origin = value ? 'ai_extracted' : 'manual'
          } else if (field.key === 'shipment_date' || field.key === 'export_date') {
            value = new Date(kase.updated_at).toISOString().slice(0, 10)
            origin = 'system'
          } else if (field.key === 'purchase_price_statement' && lines.length > 0) {
            value = lines
              .map((line, index) => `Line ${index + 1}: ${line.line_total == null ? '0' : line.line_total}`)
              .join('\n')
            origin = 'system'
          } else if (field.key === 'total_invoice_value' && lines.length > 0) {
            value = String(lines.reduce((total, line) => total + (line.line_total ?? 0), 0))
            origin = 'system'
          } else if (field.key === 'total_charges') {
            value = '0'
            origin = 'system'
          } else if (field.key === 'validation_status') {
            value = 'Needs review'
            origin = 'system'
          } else if (field.key === 'reconciliation_status') {
            value = 'needs_manual_review'
            origin = 'system'
          } else if (field.key === 'risk_severity') {
            value = 'moderate'
            origin = 'system'
          } else if (field.key === 'extraction_source') {
            value = sourceDocs[0] ? 'Uploaded invoice extraction' : 'Manual'
            origin = 'system'
          } else if (field.key === 'ocr_confidence') {
            value = sourceDocs[0]?.extractionConfidence == null ? '' : String(sourceDocs[0].extractionConfidence)
            origin = 'system'
          } else if (field.key === 'agent_confirmed_status' || field.key === 'edited_by_agent_status') {
            value = 'N'
            origin = 'system'
          } else if (field.key === 'last_edited_by') {
            value = actor
            origin = 'system'
          } else if (field.key === 'last_edited_timestamp') {
            value = new Date(kase.updated_at).toISOString().slice(0, 16).replace('T', ' ')
            origin = 'system'
          } else {
            value = pickField(fields, field.key)
            origin = value ? 'ai_extracted' : 'manual'
          }
          break
        case 'permit_license':
          if (field.key === 'status') {
            value = 'requested'
            origin = 'system'
          }
          break
      }

      valueMap[field.key] = seedField(value, origin, actor)
    }
  }

  return {
    id: crypto.randomUUID(),
    caseId: kase.id,
    kind: 'structured',
    documentType,
    title: definition.label,
    status: 'draft',
    version: 1,
    schemaVersion: definition.schemaVersion,
    createdAt: nowIso(),
    createdBy: actor,
    lastUpdatedAt: nowIso(),
    lastUpdatedBy: actor,
    sourceLinks: sharedSourceIds,
    fields: valueMap,
    lineItems:
      documentType === 'cbp_7501'
        ? Array.from({ length: Math.max(lines.length, 1) }, (_, index) =>
            build7501LineItem(lines, actor, index),
          )
        : documentType === 'manifest'
          ? Array.from({ length: Math.max(lines.length, 1) }, (_, index) =>
              buildManifestShipmentLine(lines, fields, actor, index),
            )
        : documentType === 'commercial_invoice'
          ? Array.from({ length: Math.max(lines.length, 1) }, (_, index) =>
              buildCommercialInvoiceLineItem(lines, fields, actor, index),
            )
        : [],
    sectionStates: buildSectionStates(actor, documentType),
    awaRecommendations: [],
    validationIssues: [],
    auditHistory: defaultAudit(actor, `${definition.label} scaffolded for case workspace`),
  }
}

export function mapSourceDocuments(
  docs: IntakeDocument[],
  actorLabel: string,
): SourceDocumentRecord[] {
  return docs.map((doc) => {
    const classification = classifySourceDocument(doc)
    return {
      id: doc.id,
      kind: 'source',
      title: doc.original_filename,
      storagePath: doc.storage_path,
      documentTypeLabel: labelForDocumentType(classification.documentType),
      mirrorsDocumentType: classification.documentType,
      linkedStructuredDocumentId: classification.documentType ? `pending-${classification.documentType}` : null,
      classificationMethod: classification.method,
      autoPopulationStatus: resolveSourceAutoPopulationStatus({
        extractionStatus: doc.ocr_status,
        mirrorsDocumentType: classification.documentType,
        autoPopulatedFieldCount: 0,
        conflictFieldCount: 0,
      }),
      autoPopulatedFieldCount: 0,
      conflictFieldCount: 0,
      sourceType: 'upload',
      status: doc.ocr_status === 'error' ? 'needs_attention' : doc.ocr_status === 'done' ? 'complete' : 'in_progress',
      mimeType: doc.mime_type,
      sizeLabel: formatBytes(doc.size_bytes),
      uploadedAt: doc.uploaded_at,
      uploadedBy: actorLabel,
      extractionStatus: doc.ocr_status,
      extractionConfidence: doc.ocr_confidence,
    }
  })
}

export function buildInitialStructuredDocuments(params: {
  kase: Case
  actorLabel: string
  fields: EntryField[]
  lines: EntryLine[]
  sourceDocs: SourceDocumentRecord[]
}) {
  const { kase, actorLabel, fields, lines, sourceDocs } = params
  return DOCUMENT_TYPE_ORDER.map((documentType) =>
    buildStructuredDocument(kase, actorLabel, documentType, fields, lines, sourceDocs),
  )
}

export function buildInitialReviewState(caseId: string): CaseReviewState {
  return {
    caseId,
    readinessStatus: 'missing_required_documents',
    readinessSummary: 'Required documents are still missing or incomplete, so the case is not ready for review yet.',
    isReadyForReview: false,
    readinessChecks: [],
    missingRequirements: [],
    incompleteDocuments: [],
    documentSummaries: [],
    awaStatus: 'not_started',
    initiatedAt: null,
    initiatedBy: null,
    findings: [],
    riskSummary: {
      total: 0,
      open: 0,
      high: 0,
    },
    chatThreadId: `${caseId}-awa-thread`,
    chatMessages: [
      {
        id: `${caseId}-chat-1`,
        role: 'assistant',
        sentAt: nowIso(),
        text: 'I can help compare structured records, explain flags, and track what still needs review before submission to ACE.',
      },
    ],
    reviewDecisions: [],
    reviewHistory: defaultAudit('VICE', 'Review workspace scaffolded'),
  }
}

export function attachMockRecommendations(records: StructuredDocumentRecord[]) {
  return records.map((record) => {
    const recommendations: AwaFieldRecommendation[] = []

    return {
      ...record,
      awaRecommendations: recommendations,
    }
  })
}

export function summarizeValidationIssues(issues: FieldValidationIssue[]) {
  return {
    errors: issues.filter((issue) => issue.severity === 'error').length,
    warnings: issues.filter((issue) => issue.severity === 'warning').length,
  }
}
